import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import Spinner from '../components/Spinner';
import { BswGonder, BswProformaListesi, durumu } from '../components/BswProforma';

function beep(type = 'ok') {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain); gain.connect(ctx.destination);
    if (type === 'ok') {
      osc.frequency.value = 880;
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.15);
      osc.start(ctx.currentTime); osc.stop(ctx.currentTime + 0.15);
    } else if (type === 'dup') {
      osc.frequency.value = 440;
      gain.gain.setValueAtTime(0.2, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.25);
      osc.start(ctx.currentTime); osc.stop(ctx.currentTime + 0.25);
    } else {
      osc.frequency.value = 220;
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
      osc.start(ctx.currentTime); osc.stop(ctx.currentTime + 0.3);
    }
  } catch { /* ses yoksa geç */ }
}

const getToken = () => localStorage.getItem('ozon_token');
const authHdr  = () => ({ Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' });

async function api(method, path, body) {
  const r = await fetch(`/api${path}`, {
    method, headers: authHdr(),
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || r.statusText);
  return data;
}

function fmtDt(s) {
  if (!s) return '—';
  return new Date(s).toLocaleString('tr-TR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

const SUPPORTED_FORMATS = [
  Html5QrcodeSupportedFormats.QR_CODE,
  Html5QrcodeSupportedFormats.CODE_128,
  Html5QrcodeSupportedFormats.CODE_39,
  Html5QrcodeSupportedFormats.EAN_13,
  Html5QrcodeSupportedFormats.EAN_8,
  Html5QrcodeSupportedFormats.DATA_MATRIX,
  Html5QrcodeSupportedFormats.PDF_417,
];

const STATUS_COLOR = {
  delivered:            'text-green-600 bg-green-50',
  cancelled:            'text-red-500 bg-red-50',
  awaiting_packaging:   'text-blue-600 bg-blue-50',
  awaiting_deliver:     'text-purple-600 bg-purple-50',
  ready_to_ship:        'text-indigo-600 bg-indigo-50',
  delivering:           'text-cyan-600 bg-cyan-50',
  kargo_hazir:          'text-teal-600 bg-teal-50',
};

// ── Kamera Tarayıcı ───────────────────────────────────────────────────────────
function CameraScanner({ onScan, onClose }) {
  const onScanRef   = useRef(onScan);
  const [flash, setFlash] = useState(false);
  useEffect(() => { onScanRef.current = onScan; }, [onScan]);

  useEffect(() => {
    const scanner = new Html5Qrcode('qr-reader', { formatsToSupport: SUPPORTED_FORMATS, verbose: false });
    scanner.start(
      { facingMode: 'environment' },
      { fps: 25, qrbox: undefined },
      (text) => {
        setFlash(true);
        setTimeout(() => setFlash(false), 300);
        if (navigator.vibrate) navigator.vibrate([80]);
        onScanRef.current(text);
      },
      () => {}
    ).catch(e => console.warn('[Scanner]', e));
    return () => { scanner.isScanning && scanner.stop().catch(() => {}); };
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black">
      {flash && <div className="absolute inset-0 bg-green-400/50 z-10 pointer-events-none" />}
      <div className="relative z-20 flex items-center justify-between px-4 py-3 bg-black/70">
        <div>
          <p className="text-white text-sm font-semibold">Barkodu kameraya tut</p>
          <p className="text-white/50 text-xs">Okununca yeşil yanar + titreşir</p>
        </div>
        <button onClick={onClose}
          className="text-white bg-white/20 rounded-xl px-4 py-2 text-sm font-semibold active:bg-white/40">
          ✕ Kapat
        </button>
      </div>
      <div id="qr-reader" className="flex-1 w-full" />
    </div>
  );
}

// ── Oturum Listesi ────────────────────────────────────────────────────────────
function SessionList({ onSelect, onNew }) {
  const [sessions, setSessions] = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [bswDurum, setBswDurum] = useState({});   // oturum_id → BSW proforma durumu
  const [bswPanel, setBswPanel] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setSessions(await api('GET', '/scan/sessions?limit=50')); }
    catch { setSessions([]); }
    finally { setLoading(false); }
    api('GET', '/bsw-proforma/oturum-durumlari').then(setBswDurum).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  async function del(id, e) {
    e.stopPropagation();
    if (!window.confirm('Bu oturumu silmek istiyor musun?')) return;
    try { await api('DELETE', `/scan/sessions/${id}`); } catch (err) { alert(err.message); return; }
    load();
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Tedarik Takibi</h1>
          <p className="text-xs text-gray-400 mt-0.5">Kimden ne aldığını kaydet, borcunu takip et</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setBswPanel(v => !v)}
            className="bg-white border border-indigo-200 text-indigo-700 text-sm font-semibold px-4 py-2 rounded-xl hover:bg-indigo-50 transition-colors">
            🧾 BSW'ye gönder
          </button>
          <button onClick={onNew}
            className="bg-blue-600 text-white text-sm font-semibold px-4 py-2 rounded-xl hover:bg-blue-700 transition-colors">
            + Yeni Oturum
          </button>
        </div>
      </div>

      {bswPanel && <BswGonder onGonderildi={load} onKapat={() => setBswPanel(false)} />}

      {loading && <div className="flex justify-center py-12"><Spinner size="lg" /></div>}
      {!loading && sessions?.length === 0 && (
        <div className="text-center py-16 text-gray-400">
          <div className="text-4xl mb-3">📦</div>
          <p className="text-sm">Henüz hiç oturum yok</p>
          <p className="text-xs mt-1">Yeni oturum başlatarak taramaya başla</p>
        </div>
      )}

      <div className="space-y-2">
        {sessions?.map(s => (
          <button key={s.id} onClick={() => onSelect(s)}
            className="w-full text-left bg-white border border-gray-100 rounded-xl p-4 hover:border-blue-200 hover:shadow-sm transition-all group">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-gray-900">
                    {s.note || fmtDt(s.created_at)}
                  </span>
                  <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full font-medium">
                    {s.scan_count} sipariş
                  </span>
                  {bswDurum[s.id]?.gonderilmemis > 0 && (
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-medium">
                      BSW: {bswDurum[s.id].gonderilmemis} okutma gönderilmedi
                    </span>
                  )}
                  {bswDurum[s.id]?.mukerrer > 0 && (
                    <span title="Bu siparişler başka oturumda da okutulmuş; BSW'ye bir kez gider"
                          className="text-xs bg-orange-50 text-orange-700 px-2 py-0.5 rounded-full font-medium">
                      {bswDurum[s.id].mukerrer} mükerrer
                    </span>
                  )}
                  {bswDurum[s.id]?.talepler.map(t => {
                    const d = durumu(t);
                    return <span key={t.id} className={`text-xs px-2 py-0.5 rounded-full font-medium ${d.cls}`}>BSW: {d.ad}</span>;
                  })}
                </div>
                <p className="text-xs text-gray-300 mt-0.5">{fmtDt(s.created_at)}</p>
              </div>
              <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={(e) => { e.stopPropagation(); downloadSessionCSV(s); }}
                  className="text-xs text-green-600 hover:text-green-800 px-2 py-1 rounded hover:bg-green-50">
                  CSV
                </button>
                <button onClick={(e) => del(s.id, e)}
                  className="text-xs text-red-400 hover:text-red-600 px-2 py-1 rounded hover:bg-red-50">
                  Sil
                </button>
                <span className="text-gray-300">›</span>
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Yeni Oturum Modal — sadece opsiyonel not ──────────────────────────────────
function NewSessionModal({ onCreated, onClose }) {
  const [note,   setNote]   = useState('');
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');

  async function submit() {
    setSaving(true); setError('');
    try {
      // source_name artık zorunlu değil — backend'e boş string gönder
      const session = await api('POST', '/scan/sessions', { source_name: note.trim() || 'Genel', note: note.trim() || null });
      onCreated(session);
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-bold text-gray-900">Yeni Tarama Oturumu</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
        </div>
        {error && <p className="text-xs text-red-500 bg-red-50 p-2 rounded-lg">{error}</p>}
        <div>
          <label className="text-xs font-semibold text-gray-500 block mb-1.5">İsim / Not (isteğe bağlı)</label>
          <input value={note} onChange={e => setNote(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && submit()}
            placeholder="Örn: Haziran 2. parti"
            className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-blue-400"
            autoFocus />
          <p className="text-[11px] text-gray-400 mt-1.5">
            Boş bırakırsan tarih/saat ile kaydedilir.
          </p>
        </div>
        <button onClick={submit} disabled={saving}
          className="w-full bg-blue-600 text-white font-semibold py-3 rounded-xl hover:bg-blue-700 disabled:opacity-40 transition-colors">
          {saving ? 'Oluşturuluyor...' : 'Taramaya Başla →'}
        </button>
      </div>
    </div>
  );
}

// ── Taranmış sipariş satırı ───────────────────────────────────────────────────
// item_sources değerini yorumla: "Adem" → {Adem: qty} | {Adem:1, Semavi:1} → olduğu gibi
function parseSrcVal(val, totalQty) {
  if (!val) return {};
  if (typeof val === 'string') return { [val]: totalQty };
  if (typeof val === 'object') return val;
  return {};
}

// Miktar bölme satırı (qty > 1 ürünler için)
function SplitRow({ item, srcVal, sources, onSave, onCancel }) {
  const parsed = parseSrcVal(srcVal, item.quantity);
  const initQtys = () => {
    const q = {};
    sources.forEach(s => { q[s.name] = parsed[s.name] || 0; });
    q['__kendim__'] = parsed['__kendim__'] || 0;
    return q;
  };
  const [qtys, setQtys] = useState(initQtys);
  const total = Object.values(qtys).reduce((a, b) => a + (parseInt(b) || 0), 0);
  const ok    = total === item.quantity;

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-xl p-2.5 space-y-2">
      <div className="text-[11px] font-semibold text-blue-700">
        Böl — toplam {item.quantity} adet, girilen: <span className={ok ? 'text-green-600' : 'text-red-500'}>{total}</span>
      </div>
      <div className="flex flex-wrap gap-2">
        {sources.map(s => (
          <label key={s.name} className="flex items-center gap-1">
            <span className="text-[11px] text-gray-600 font-medium">{s.name}</span>
            <input type="number" min="0" max={item.quantity}
              value={qtys[s.name] || ''}
              onChange={e => setQtys(prev => ({ ...prev, [s.name]: parseInt(e.target.value) || 0 }))}
              className="w-12 border border-gray-300 rounded-lg px-1.5 py-0.5 text-xs text-center focus:outline-none focus:border-blue-400"
            />
          </label>
        ))}
        <label className="flex items-center gap-1">
          <span className="text-[11px] text-gray-500 font-medium">Kendim</span>
          <input type="number" min="0" max={item.quantity}
            value={qtys['__kendim__'] || ''}
            onChange={e => setQtys(prev => ({ ...prev, '__kendim__': parseInt(e.target.value) || 0 }))}
            className="w-12 border border-gray-300 rounded-lg px-1.5 py-0.5 text-xs text-center focus:outline-none focus:border-blue-400"
          />
        </label>
      </div>
      <div className="flex gap-2">
        <button onClick={() => {
          // Sıfır olanları çıkar, kaydet
          const result = Object.fromEntries(Object.entries(qtys).filter(([, v]) => v > 0));
          onSave(result);
        }} disabled={!ok}
          className="px-3 py-1 text-xs font-semibold bg-blue-600 text-white rounded-lg disabled:opacity-40">
          Kaydet
        </button>
        <button onClick={onCancel} className="px-3 py-1 text-xs text-gray-500 border border-gray-200 rounded-lg">İptal</button>
      </div>
    </div>
  );
}

function ScanEventRow({ ev, idx, sources, defaultSource, onRemove, onSourceChange }) {
  const [localSources, setLocalSources] = useState(ev.item_sources || {});
  const [splitOpen,    setSplitOpen]    = useState(null); // offer_id or null
  const items = ev.items || [];

  useEffect(() => {
    setLocalSources(ev.item_sources || {});
  }, [ev.item_sources]);

  async function saveItemSource(offerId, value) {
    // value: null = kaldır, string = tek kaynak, object = bölünmüş
    const next = value === null
      ? Object.fromEntries(Object.entries(localSources).filter(([k]) => k !== offerId))
      : { ...localSources, [offerId]: value };
    setLocalSources(next);
    setSplitOpen(null);
    try {
      const r = await fetch(`/api/scan/events/${ev.id}/item-sources`, {
        method: 'PATCH',
        headers: authHdr(),
        body: JSON.stringify({ item_sources: next }),
      });
      const d = await r.json();
      if (!r.ok) { setLocalSources(ev.item_sources || {}); alert(d.error || 'Kaydedilemedi'); return; }
      onSourceChange(d);
    } catch { /* sessiz geç */ }
  }

  const hasItems   = items.length > 0;
  const unassigned = items.filter(it => !localSources[it.offer_id]);

  // Bir ürünün atama özetini metne çevir
  function srcLabel(offerId, totalQty) {
    const val = localSources[offerId];
    if (!val) return null;
    if (typeof val === 'string') return val;
    // Bölünmüş: {Adem:1, Semavi:1}
    return Object.entries(val)
      .filter(([, q]) => q > 0)
      .map(([src, q]) => `${src === '__kendim__' ? 'Kendim' : src}:${q}`)
      .join(' / ');
  }

  return (
    <div className="border-b border-gray-50 last:border-0">
      <div className="px-4 py-3 flex items-start gap-3 group">
        <span className="text-xs text-gray-300 w-5 text-right flex-shrink-0 mt-0.5">{idx}</span>
        <div className="flex-1 min-w-0">

          {/* Sipariş başlık */}
          <div className="flex items-center gap-2 flex-wrap">
            <Link to={`/orders/${ev.posting_number}`}
              className="font-mono text-xs font-semibold text-blue-600 hover:underline">
              {ev.posting_number}
            </Link>
            {ev.order_status ? (
              <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${STATUS_COLOR[ev.order_status] || 'text-gray-500 bg-gray-50'}`}>
                {ev.order_status}
              </span>
            ) : (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-yellow-50 text-yellow-600 font-medium">Sistemde yok</span>
            )}
            {hasItems && unassigned.length === 0 && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-50 text-green-600 font-medium">✓ tümü atandı</span>
            )}
            {hasItems && unassigned.length > 0 && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-orange-50 text-orange-500 font-medium">{unassigned.length} atanmadı</span>
            )}
          </div>

          {/* Ürün satırları */}
          {hasItems && (
            <div className="mt-2 space-y-2 pl-1 border-l-2 border-gray-100">
              {items.map(item => {
                const val       = localSources[item.offer_id];
                const isSplit   = val && typeof val === 'object';
                const isSingle  = val && typeof val === 'string';
                const isKendim  = !val;
                const isSplitting = splitOpen === item.offer_id;
                const canSplit  = item.quantity > 1;

                return (
                  <div key={item.offer_id} className="space-y-1.5">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-[11px] text-gray-600 shrink-0">
                        <span className="font-semibold text-gray-800">{item.quantity}×</span> {item.offer_id}
                      </span>
                      <span className="text-[10px] text-gray-300">→</span>

                      {/* Tek kaynak butonları — split modunda gizle */}
                      {!isSplitting && (
                        <>
                          {sources.map(s => (
                            <button key={s.name} onClick={() => saveItemSource(item.offer_id, s.name)}
                              className={`text-[10px] px-2 py-0.5 rounded-lg font-semibold transition-colors ${
                                isSingle && val === s.name
                                  ? 'bg-blue-600 text-white shadow-sm'
                                  : 'bg-gray-100 text-gray-500 hover:bg-blue-50 hover:text-blue-600'
                              }`}>
                              {s.name}
                            </button>
                          ))}
                          <button onClick={() => saveItemSource(item.offer_id, null)}
                            className={`text-[10px] px-2 py-0.5 rounded-lg font-semibold transition-colors ${
                              isKendim ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                            }`}>
                            Kendim
                          </button>
                          {canSplit && (
                            <button onClick={() => setSplitOpen(item.offer_id)}
                              className={`text-[10px] px-2 py-0.5 rounded-lg font-semibold transition-colors ${
                                isSplit ? 'bg-purple-600 text-white' : 'bg-purple-50 text-purple-600 hover:bg-purple-100'
                              }`}>
                              {isSplit ? `⋮ ${srcLabel(item.offer_id, item.quantity)}` : '⋮ Böl'}
                            </button>
                          )}
                          {isKendim && !isSplit && (
                            <span className="text-[10px] text-gray-300 italic">kendi stoğu</span>
                          )}
                        </>
                      )}
                    </div>

                    {/* Bölme UI */}
                    {isSplitting && (
                      <SplitRow
                        item={item}
                        srcVal={val}
                        sources={sources}
                        onSave={(result) => saveItemSource(item.offer_id, result)}
                        onCancel={() => setSplitOpen(null)}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <p className="text-[10px] text-gray-300 mt-1.5">{fmtDt(ev.scanned_at)}</p>
        </div>
        <button onClick={onRemove}
          className="text-gray-200 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all flex-shrink-0 text-lg leading-none mt-0.5">
          ×
        </button>
      </div>
    </div>
  );
}

// ── Aktif Oturum ──────────────────────────────────────────────────────────────
function ScanSession({ session, onBack }) {
  const [events,      setEvents]      = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [input,       setInput]       = useState('');
  const [busy,        setBusy]        = useState(false);
  const [flash,       setFlash]       = useState(null);
  const [camOpen,     setCamOpen]     = useState(false);
  const [sources,     setSources]     = useState([]);
  const [defSource,   setDefSource]   = useState('');
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, srcs] = await Promise.all([
        api('GET', `/scan/sessions/${session.id}`),
        api('GET', '/scan/sources'),
      ]);
      setEvents(d.events || []);
      setSources(srcs);
      // Varsayılan kaynak BSW — Hilal'in tedarikçisi; yoksa ilk kaynak
      if (srcs.length > 0 && !defSource) setDefSource((srcs.find(x => x.name === 'BSW') || srcs[0]).name);
    } catch { setEvents([]); }
    finally { setLoading(false); }
  }, [session.id]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const t = setTimeout(() => inputRef.current?.focus(), 100);
    return () => clearTimeout(t);
  }, []); // sadece mount'ta bir kez focus

  function showFlash(type, msg) {
    setFlash({ type, msg });
    setTimeout(() => setFlash(null), msg.length > 60 ? 6000 : 2500);
  }

  // Barkod okutunca: kaydet + tüm ürünlere varsayılan kaynağı otomatik ata
  const scan = useCallback(async (pn) => {
    if (!pn.trim() || busy) return;
    setBusy(true);
    try {
      const ev = await api('POST', `/scan/sessions/${session.id}/scan`, { posting_number: pn.trim() });

      // Varsayılan kaynak varsa tüm ürünlere otomatik ata
      let finalEv = ev;
      if (defSource && ev.items?.length > 0) {
        const autoSources = {};
        ev.items.forEach(it => { autoSources[it.offer_id] = defSource; });
        await fetch(`/api/scan/events/${ev.id}/item-sources`, {
          method: 'PATCH', headers: authHdr(),
          body: JSON.stringify({ item_sources: autoSources }),
        });
        finalEv = { ...ev, item_sources: autoSources };
      }

      setEvents(prev => [finalEv, ...prev]);
      beep('ok');
      if (ev.baska_oturumlar?.length) {
        // Aynı sipariş başka oturumda da var: eklendi ama BSW'ye ikinci kez gönderilmez
        const ilk = ev.baska_oturumlar[0];
        beep('dup');
        showFlash('dup', `⚠ Eklendi ama bu sipariş "${ilk.ad}" oturumunda da okutulmuş` +
          (ev.baska_oturumlar.some(o => o.bswye_gitti) ? ' ve BSW\'ye gönderilmiş' : '') +
          ' — BSW\'ye ikinci kez gönderilmez. Yanlışlıkla okuttuysan sil.');
      } else {
        showFlash(ev.exists_in_db ? 'ok' : 'new',
          ev.exists_in_db ? `✓ Eklendi (${ev.order_status})` : `★ Eklendi — sistemde yok`);
      }
    } catch (e) {
      if (e.message.includes('zaten okutuldu')) {
        beep('dup'); showFlash('dup', `⚠ Zaten okutuldu`);
      } else {
        beep('err'); showFlash('err', `✗ ${e.message}`);
      }
    } finally {
      setBusy(false); setInput('');
      inputRef.current?.focus();
    }
  }, [session.id, busy, defSource]);

  const lastCam = useRef('');
  const handleCamScan = useCallback((decoded) => {
    if (decoded === lastCam.current) return;
    lastCam.current = decoded;
    setTimeout(() => { lastCam.current = ''; }, 3000);
    scan(decoded);
  }, [scan]);

  async function removeEvent(id) {
    try { await api('DELETE', `/scan/events/${id}`); } catch (err) { alert(err.message); return; }
    setEvents(prev => prev.filter(e => e.id !== id));
  }

  const flashColors = {
    ok:  'bg-green-50 border-green-300 text-green-800',
    new: 'bg-yellow-50 border-yellow-300 text-yellow-800',
    dup: 'bg-orange-50 border-orange-300 text-orange-800',
    err: 'bg-red-50 border-red-300 text-red-700',
  };

  return (
    <div className="space-y-3">
      {/* Kamera overlay */}
      {camOpen && <CameraScanner onScan={handleCamScan} onClose={() => setCamOpen(false)} />}
      {/* Başlık */}
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="text-gray-400 hover:text-gray-700 text-xl">‹</button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="font-bold text-gray-900 truncate">{session.note || fmtDt(session.created_at)}</h1>
            <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full font-semibold flex-shrink-0">
              {events.length}
            </span>
          </div>
          <p className="text-xs text-gray-300">{fmtDt(session.created_at)}</p>
        </div>
      </div>

      {/* Flash */}
      {flash && (
        <div className={`border rounded-xl px-4 py-2.5 text-sm font-medium ${flashColors[flash.type]}`}>
          {flash.msg}
        </div>
      )}

      {/* Varsayılan kaynak seçici */}
      {sources.length > 0 && (
        <div className="bg-white border border-gray-100 rounded-2xl p-4 shadow-sm">
          <p className="text-xs font-semibold text-gray-500 mb-2">
            Varsayılan kaynak — okutulunca ürünlere otomatik atanır
          </p>
          <div className="flex gap-2 flex-wrap">
            {sources.map(s => (
              <button key={s.name} onClick={() => setDefSource(s.name)}
                className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${
                  defSource === s.name
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}>
                {s.name}
              </button>
            ))}
            <button onClick={() => setDefSource('')}
              className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${
                defSource === ''
                  ? 'bg-gray-700 text-white'
                  : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
              }`}>
              Atama
            </button>
          </div>
          {defSource && (
            <p className="text-[11px] text-blue-500 mt-2">
              Her okutmada tüm ürünler <strong>{defSource}</strong>'e atanır. Farklıysa altta değiştir.
            </p>
          )}
        </div>
      )}

      {/* Barkod input */}
      <div className="bg-white border-2 border-blue-200 rounded-2xl p-4 shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs font-semibold text-blue-600">Barkod / QR Okut</label>
          <button onClick={() => setCamOpen(true)}
            className="flex items-center gap-1.5 bg-blue-600 text-white text-xs font-semibold px-3 py-1.5 rounded-lg hover:bg-blue-700">
            📷 Kamera
          </button>
        </div>
        <div className="flex gap-2">
          <input ref={inputRef} value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && scan(input)}
            placeholder="Sipariş numarası..."
            className="flex-1 border border-gray-200 rounded-xl px-3 py-3 text-sm font-mono focus:outline-none focus:border-blue-400"
            autoComplete="off" autoCorrect="off" spellCheck={false} />
          <button onClick={() => scan(input)} disabled={busy || !input.trim()}
            className="px-4 py-3 bg-blue-600 text-white rounded-xl font-semibold text-sm hover:bg-blue-700 disabled:opacity-40">
            {busy ? '...' : 'Okut'}
          </button>
        </div>
      </div>

      {/* Taranan liste */}
      {loading && <div className="flex justify-center py-8"><Spinner size="md" /></div>}
      {!loading && events.length === 0 && (
        <div className="text-center py-10 text-gray-400">
          <div className="text-3xl mb-2">🔍</div>
          <p className="text-sm">Henüz tarama yok</p>
        </div>
      )}
      {!loading && events.length > 0 && (
        <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-sm">
          <div className="px-4 py-3 border-b border-gray-50 flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-500 uppercase">Taranan Siparişler</span>
            <span className="text-xs text-gray-400">{events.length} adet</span>
          </div>
          <div className="divide-y divide-gray-50 max-h-[55vh] overflow-y-auto">
            {events.map((ev, i) => (
              <ScanEventRow
                key={ev.id}
                ev={ev}
                idx={events.length - i}
                sources={sources}
                defaultSource={defSource}
                onRemove={() => removeEvent(ev.id)}
                onSourceChange={(updated) =>
                  setEvents(prev => prev.map(e => e.id === updated.id ? { ...e, item_sources: updated.item_sources } : e))
                }
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Rapor Tablosu (2. Tab) ────────────────────────────────────────────────────
// ── Maliyet Giriş Modalı ─────────────────────────────────────────────────────
function CostModal({ rows, onClose, onSaved }) {
  const [costs, setCosts] = useState(() => {
    const map = {};
    rows.forEach(r => { if (!map[r.offer_id]) map[r.offer_id] = String(r.unit_cost || ''); });
    return map;
  });
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      const payload = Object.entries(costs)
        .filter(([, v]) => v !== '')
        .map(([offer_id, unit_cost]) => ({ offer_id, unit_cost: parseFloat(unit_cost) || 0 }));
      await api('PATCH', '/scan/product-costs', { costs: payload });
      onSaved();
      onClose();
    } catch (e) {
      alert('Kayıt hatası: ' + e.message);
    } finally { setSaving(false); }
  }

  // unique offer_ids from selected rows
  const unique = [];
  const seen = new Set();
  rows.forEach(r => { if (!seen.has(r.offer_id)) { seen.add(r.offer_id); unique.push(r); } });

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <div className="font-bold text-gray-800">Maliyet Gir</div>
            <div className="text-xs text-gray-400 mt-0.5">{unique.length} ürün çeşidi · Birim fiyat (₺)</div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
        </div>
        <div className="px-5 py-3 max-h-80 overflow-y-auto space-y-3">
          {unique.map(r => (
            <div key={r.offer_id} className="flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold text-gray-800 truncate">{r.offer_id}</div>
                {r.product_name && <div className="text-[11px] text-gray-400 truncate">{r.product_name}</div>}
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={costs[r.offer_id] ?? ''}
                  onChange={e => setCosts(prev => ({ ...prev, [r.offer_id]: e.target.value }))}
                  placeholder="0.00"
                  className="w-24 border border-gray-200 rounded-xl px-2.5 py-1.5 text-sm text-right focus:outline-none focus:border-blue-400"
                />
                <span className="text-xs text-gray-400">₺</span>
              </div>
            </div>
          ))}
        </div>
        <div className="px-5 py-4 border-t border-gray-100 flex gap-2 justify-end">
          <button onClick={onClose}
            className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700 border border-gray-200 rounded-xl">
            İptal
          </button>
          <button onClick={save} disabled={saving}
            className="px-5 py-2 text-sm font-semibold bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50">
            {saving ? 'Kaydediliyor...' : 'Kaydet'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Oturum CSV indirme ───────────────────────────────────────────────────────
async function downloadSessionCSV(session) {
  const data = await api('GET', `/scan/sessions/${session.id}`);
  const events = data.events || [];
  const headers = ['Sipariş No', 'Tarih', 'Sipariş Durumu', 'Ürün Kodu', 'Ürün Adı', 'Adet', 'Kaynak'];
  const lines = [headers.join(';')];
  events.forEach(ev => {
    const sources = ev.item_sources || {};
    const items = ev.items || [];
    if (items.length === 0) {
      lines.push([ev.posting_number, ev.scanned_at?.slice(0,10), ev.order_status || '', '', '', '', ''].join(';'));
    } else {
      items.forEach(it => {
        const srcVal = sources[it.offer_id];
        let kaynak = '';
        if (srcVal === null || srcVal === undefined) kaynak = 'Kendim';
        else if (typeof srcVal === 'string') kaynak = srcVal;
        else if (typeof srcVal === 'object') kaynak = Object.entries(srcVal).map(([k, v]) => `${k}:${v}`).join(' / ');
        lines.push([
          ev.posting_number,
          ev.scanned_at?.slice(0,10),
          ev.order_status || '',
          it.offer_id,
          it.name || '',
          it.quantity,
          kaynak,
        ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(';'));
      });
    }
  });
  const bom = '﻿';
  const blob = new Blob([bom + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `oturum-${session.id}-${(session.note || session.created_at?.slice(0,10)).replace(/[^a-zA-Z0-9-_]/g, '_')}.csv`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

// ── CSV indirme yardımcısı ────────────────────────────────────────────────────
function downloadCSV(rows, summary) {
  const headers = ['Kaynak', 'Ürün Kodu', 'Ürün Adı', 'Sipariş Adedi', 'Toplam Adet', 'Birim Maliyet (₺)', 'Toplam Maliyet (₺)', 'Ödenen Adet', 'Ödenmemiş Adet', 'Son Tarama'];
  const lines = [headers.join(';')];
  rows.forEach(r => {
    lines.push([
      r.source_name,
      r.offer_id,
      r.product_name || '',
      r.siparis_adedi,
      r.toplam_adet,
      r.unit_cost.toFixed(2),
      r.toplam_maliyet.toFixed(2),
      r.paid_adet ?? 0,
      r.unpaid_adet ?? 0,
      r.son_tarama ? new Date(r.son_tarama).toLocaleDateString('tr-TR') : '',
    ].join(';'));
  });
  lines.push('');
  lines.push(['Kaynak', 'Toplam Sipariş', 'Ürün Çeşidi', 'Toplam Adet', 'Toplam Borç (₺)', 'Ödenmemiş Borç (₺)'].join(';'));
  summary.forEach(s => {
    lines.push([s.source_name, s.siparis_adedi, s.urun_cesidi, s.toplam_adet, (s.toplam_borc||0).toFixed(2), (s.odenmemis_borc||0).toFixed(2)].join(';'));
  });
  const blob = new Blob(['﻿' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `tedarik-raporu-${new Date().toISOString().slice(0,10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Sipariş bazlı CSV indirme ─────────────────────────────────────────────────
async function downloadOrderReportCSV(from, to) {
  const qs = new URLSearchParams();
  if (from) qs.set('from', from);
  if (to)   qs.set('to', to);
  const rows = await api('GET', `/scan/order-report?${qs}`);
  const headers = ['Sipariş No', 'Tarama Tarihi', 'Kullanıcı', 'Oturum', 'Ödendi', 'Ürün Kodu', 'Ürün Adı', 'Adet', 'Kaynak'];
  const lines = [headers.join(';')];
  rows.forEach(r => {
    let kaynak = '';
    const raw = r.kaynak_raw;
    if (raw === null || raw === undefined) kaynak = '';
    else if (typeof raw === 'string') kaynak = raw;
    else if (typeof raw === 'object') kaynak = Object.entries(raw).map(([k,v])=>`${k}:${v}`).join(' / ');
    lines.push([
      r.posting_number,
      r.scanned_at ? new Date(r.scanned_at).toLocaleDateString('tr-TR') : '',
      r.kullanici || '',
      r.oturum || '',
      r.paid ? 'Evet' : 'Hayır',
      r.offer_id || '',
      r.product_name || '',
      r.quantity || '',
      kaynak,
    ].map(v => `"${String(v).replace(/"/g,'""')}"`).join(';'));
  });
  const blob = new Blob(['﻿' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `siparis-raporu-${new Date().toISOString().slice(0,10)}.csv`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

// ── Tedarik Raporu ───────────────────────────────────────────────────────────
function ScanReport() {
  const [rows,      setRows]      = useState([]);
  const [summary,   setSummary]   = useState([]);
  const [sources,   setSources]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [filterSrc, setFilterSrc] = useState('');
  const [filterQ,   setFilterQ]   = useState('');
  const [from,      setFrom]      = useState('');
  const [to,        setTo]        = useState('');
  const [sortKey,   setSortKey]   = useState('toplam_adet');
  const [sortDir,   setSortDir]   = useState('desc');
  const [subTab,    setSubTab]    = useState('all'); // 'all' | 'unpaid' | 'paid'
  const [selected,  setSelected]  = useState(new Set()); // "source_name|offer_id" keys
  const [costModal, setCostModal] = useState(false);
  const [marking,   setMarking]   = useState(false);

  const paidParam = subTab === 'paid' ? 'true' : subTab === 'unpaid' ? 'false' : undefined;

  const load = useCallback(async () => {
    setLoading(true);
    setSelected(new Set());
    try {
      const qs = new URLSearchParams();
      if (filterSrc) qs.set('source', filterSrc);
      if (from)      qs.set('from', from);
      if (to)        qs.set('to', to);
      if (paidParam !== undefined) qs.set('paid', paidParam);
      const [rep, srcs] = await Promise.all([
        api('GET', `/scan/report?${qs}`),
        api('GET', '/scan/sources'),
      ]);
      setRows(rep.rows || []);
      setSummary(rep.summary || []);
      setSources(srcs);
    } catch { setRows([]); }
    finally { setLoading(false); }
  }, [filterSrc, from, to, paidParam]);

  useEffect(() => { load(); }, [load]);

  function toggleSort(key) {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  }

  function rowKey(r) { return `${r.source_name}|${r.offer_id}`; }

  function toggleRow(r) {
    const k = rowKey(r);
    setSelected(prev => {
      const next = new Set(prev);
      next.has(k) ? next.delete(k) : next.add(k);
      return next;
    });
  }

  function toggleAll(visRows) {
    if (visRows.every(r => selected.has(rowKey(r)))) {
      setSelected(prev => { const next = new Set(prev); visRows.forEach(r => next.delete(rowKey(r))); return next; });
    } else {
      setSelected(prev => { const next = new Set(prev); visRows.forEach(r => next.add(rowKey(r))); return next; });
    }
  }

  const filtered = rows
    .filter(r => !filterQ || r.offer_id.toLowerCase().includes(filterQ.toLowerCase()) || (r.product_name || '').toLowerCase().includes(filterQ.toLowerCase()))
    .sort((a, b) => {
      const v = sortDir === 'asc' ? 1 : -1;
      if (typeof a[sortKey] === 'number') return (a[sortKey] - b[sortKey]) * v;
      return String(a[sortKey] || '').localeCompare(String(b[sortKey] || '')) * v;
    });

  const selectedRows = filtered.filter(r => selected.has(rowKey(r)));

  async function markPaid(paid) {
    const eventIds = selectedRows.flatMap(r => r.event_ids || []);
    if (!eventIds.length) return;
    setMarking(true);
    try {
      await api('POST', '/scan/mark-paid', { event_ids: eventIds, paid });
      await load();
    } catch (e) { alert('Hata: ' + e.message); }
    finally { setMarking(false); }
  }

  function SortTh({ label, k, cls = '' }) {
    const active = sortKey === k;
    return (
      <th onClick={() => toggleSort(k)}
        className={`text-right py-2.5 px-3 text-xs font-semibold text-gray-400 uppercase cursor-pointer select-none hover:text-gray-700 ${cls}`}>
        {label} {active ? (sortDir === 'asc' ? '↑' : '↓') : ''}
      </th>
    );
  }

  const allChecked = filtered.length > 0 && filtered.every(r => selected.has(rowKey(r)));
  const totalBorc  = filtered.reduce((s, r) => s + (r.toplam_maliyet || 0), 0);

  return (
    <div className="space-y-4">
      {/* Borç özet kartları */}
      {summary.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {summary.map(s => (
            <div key={s.source_name}
              className={`rounded-xl p-3 border cursor-pointer transition-colors ${filterSrc === s.source_name ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-gray-100 hover:border-blue-200'}`}
              onClick={() => setFilterSrc(prev => prev === s.source_name ? '' : s.source_name)}>
              <div className={`text-base font-bold ${filterSrc === s.source_name ? 'text-white' : 'text-gray-800'}`}>{s.source_name}</div>
              <div className={`text-xs mt-1 space-y-0.5 ${filterSrc === s.source_name ? 'text-blue-100' : 'text-gray-400'}`}>
                <div>{s.siparis_adedi} sipariş · {s.urun_cesidi} çeşit · {s.toplam_adet} adet</div>
                {s.toplam_borc > 0 && (
                  <div className={`font-semibold ${filterSrc === s.source_name ? 'text-white' : 'text-orange-600'}`}>
                    Borç: {s.odenmemis_borc.toFixed(2)} ₺ {s.odenmemis_borc < s.toplam_borc ? `/ ${s.toplam_borc.toFixed(2)} ₺` : ''}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Sub-tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
        {[['all','Tümü'],['unpaid','Ödenmeyenler'],['paid','Ödenenler']].map(([k,label]) => (
          <button key={k} onClick={() => setSubTab(k)}
            className={`flex-1 py-2 text-xs font-semibold rounded-lg transition-colors ${subTab === k ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            {label}
          </button>
        ))}
      </div>

      {/* Filtre bar */}
      <div className="bg-white border border-gray-100 rounded-2xl p-4 shadow-sm space-y-3">
        <div className="flex gap-2 flex-wrap items-center">
          <select value={filterSrc} onChange={e => setFilterSrc(e.target.value)}
            className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-blue-400 bg-white">
            <option value="">Tüm Kaynaklar</option>
            {sources.map(s => <option key={s.name} value={s.name}>{s.name}</option>)}
          </select>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)}
            className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-blue-400" />
          <input type="date" value={to} onChange={e => setTo(e.target.value)}
            className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-blue-400" />
          {(filterSrc || from || to) && (
            <button onClick={() => { setFilterSrc(''); setFrom(''); setTo(''); }}
              className="px-3 py-2 text-xs text-gray-400 hover:text-gray-700 border border-gray-200 rounded-xl">
              Temizle
            </button>
          )}
          <div className="ml-auto flex gap-2">
            <button onClick={() => downloadOrderReportCSV(from, to)}
              className="px-3 py-2 text-xs font-semibold border border-gray-200 rounded-xl hover:border-gray-300 text-gray-600">
              📋 Sipariş Bazlı CSV
            </button>
            <button onClick={() => downloadCSV(filtered, summary)}
              className="px-3 py-2 text-xs font-semibold border border-gray-200 rounded-xl hover:border-gray-300 text-gray-600">
              📊 Özet CSV
            </button>
          </div>
        </div>
        <input value={filterQ} onChange={e => setFilterQ(e.target.value)}
          placeholder="Ürün kodu veya adı ara..."
          className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-blue-400" />
      </div>

      {/* Seçili satır aksiyonları */}
      {selected.size > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-2xl px-4 py-3 flex flex-wrap items-center gap-3">
          <span className="text-sm font-semibold text-blue-800">{selected.size} satır seçildi</span>
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => setCostModal(true)}
              className="px-3 py-1.5 text-xs font-semibold bg-white border border-blue-300 text-blue-700 rounded-xl hover:bg-blue-50">
              💰 Maliyet Gir
            </button>
            <button onClick={() => markPaid(true)} disabled={marking}
              className="px-3 py-1.5 text-xs font-semibold bg-green-600 text-white rounded-xl hover:bg-green-700 disabled:opacity-50">
              ✓ Ödendi İşaretle
            </button>
            <button onClick={() => markPaid(false)} disabled={marking}
              className="px-3 py-1.5 text-xs font-semibold bg-white border border-gray-300 text-gray-600 rounded-xl hover:bg-gray-50 disabled:opacity-50">
              ✗ Ödenmedi İşaretle
            </button>
            <button onClick={() => setSelected(new Set())}
              className="px-3 py-1.5 text-xs text-gray-400 hover:text-gray-600">
              İptal
            </button>
          </div>
          {totalBorc > 0 && (
            <span className="ml-auto text-sm font-bold text-orange-600">
              Seçili toplam: {filtered.filter(r => selected.has(rowKey(r))).reduce((s,r) => s + r.toplam_maliyet, 0).toFixed(2)} ₺
            </span>
          )}
        </div>
      )}

      {/* Tablo */}
      {loading && <div className="flex justify-center py-10"><Spinner size="lg" /></div>}
      {!loading && filtered.length === 0 && (
        <div className="text-center py-12 text-gray-400">
          <div className="text-3xl mb-2">📋</div>
          <p className="text-sm">{subTab === 'paid' ? 'Ödenmiş kayıt yok' : subTab === 'unpaid' ? 'Ödenmemiş kayıt yok' : 'Kayıtlı tarama yok'}</p>
          <p className="text-xs mt-1">Önce ürünlere kaynak ataman gerekiyor</p>
        </div>
      )}
      {!loading && filtered.length > 0 && (
        <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-sm">
          <div className="px-4 py-3 border-b border-gray-50 flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-500 uppercase">
              {filterSrc ? filterSrc : 'Tüm Kaynaklar'} — {filtered.length} ürün çeşidi
            </span>
            <span className="text-xs text-gray-400">
              {filtered.reduce((s, r) => s + r.toplam_adet, 0)} adet
              {totalBorc > 0 && <span className="ml-2 font-semibold text-orange-600">{totalBorc.toFixed(2)} ₺</span>}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[680px]">
              <thead className="border-b border-gray-50 bg-gray-50/60">
                <tr>
                  <th className="py-2.5 px-3 w-8">
                    <input type="checkbox" checked={allChecked} onChange={() => toggleAll(filtered)}
                      className="rounded cursor-pointer" />
                  </th>
                  <SortTh label="Kaynak"    k="source_name"   cls="text-left" />
                  <th className="text-left py-2.5 px-3 text-xs font-semibold text-gray-400 uppercase">Ürün</th>
                  <SortTh label="Sipariş"   k="siparis_adedi" />
                  <SortTh label="Adet"      k="toplam_adet"   />
                  <SortTh label="Birim ₺"   k="unit_cost"     />
                  <SortTh label="Toplam ₺"  k="toplam_maliyet"/>
                  <th className="text-right py-2.5 px-3 text-xs font-semibold text-gray-400 uppercase">Durum</th>
                  <SortTh label="Son Tarama" k="son_tarama"   />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map((r, i) => {
                  const isSelected = selected.has(rowKey(r));
                  const allPaid = r.unpaid_siparis === 0 && r.siparis_adedi > 0;
                  return (
                    <tr key={rowKey(r)}
                      onClick={() => toggleRow(r)}
                      className={`cursor-pointer transition-colors ${isSelected ? 'bg-blue-50' : 'hover:bg-gray-50/60'}`}>
                      <td className="py-2.5 px-3">
                        <input type="checkbox" checked={isSelected} onChange={() => {}} className="rounded pointer-events-none" />
                      </td>
                      <td className="py-2.5 px-3">
                        <span className="text-xs font-semibold bg-blue-50 text-blue-700 px-2 py-0.5 rounded-lg">
                          {r.source_name}
                        </span>
                      </td>
                      <td className="py-2.5 px-3">
                        <div className="text-xs font-semibold text-gray-800">{r.offer_id}</div>
                        {r.product_name && <div className="text-[11px] text-gray-400 truncate max-w-[180px]">{r.product_name}</div>}
                      </td>
                      <td className="py-2.5 px-3 text-right text-sm text-gray-600 font-medium">{r.siparis_adedi}</td>
                      <td className="py-2.5 px-3 text-right text-sm font-bold text-gray-800">{r.toplam_adet}</td>
                      <td className="py-2.5 px-3 text-right text-xs text-gray-500">
                        {r.unit_cost > 0 ? `${r.unit_cost.toFixed(2)} ₺` : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="py-2.5 px-3 text-right text-sm font-bold">
                        {r.toplam_maliyet > 0
                          ? <span className="text-orange-600">{r.toplam_maliyet.toFixed(2)} ₺</span>
                          : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="py-2.5 px-3 text-right">
                        {allPaid
                          ? <span className="text-xs font-semibold bg-green-100 text-green-700 px-2 py-0.5 rounded-lg">Ödendi</span>
                          : r.paid_siparis > 0
                            ? <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-lg">{r.paid_siparis}/{r.siparis_adedi} ödendi</span>
                            : <span className="text-xs bg-red-50 text-red-500 px-2 py-0.5 rounded-lg">Ödenmedi</span>}
                      </td>
                      <td className="py-2.5 px-3 text-right text-xs text-gray-400 whitespace-nowrap">
                        {r.son_tarama ? new Date(r.son_tarama).toLocaleDateString('tr-TR') : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="border-t border-gray-100 bg-gray-50/40">
                <tr>
                  <td colSpan={4} className="py-2.5 px-3 text-xs font-semibold text-gray-500">Toplam</td>
                  <td className="py-2.5 px-3 text-right text-sm font-bold text-blue-700">
                    {filtered.reduce((s, r) => s + r.toplam_adet, 0)}
                  </td>
                  <td />
                  <td className="py-2.5 px-3 text-right text-sm font-bold text-orange-600">
                    {totalBorc > 0 ? `${totalBorc.toFixed(2)} ₺` : '—'}
                  </td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {costModal && (
        <CostModal
          rows={selectedRows}
          onClose={() => setCostModal(false)}
          onSaved={load}
        />
      )}
    </div>
  );
}

// ── Ana Sayfa ─────────────────────────────────────────────────────────────────
export default function ScanInbound() {
  const [view,          setView]         = useState('list');
  const [activeSession, setActiveSession] = useState(null);
  const [tab,           setTab]          = useState('sessions'); // 'sessions' | 'report'

  return (
    <div className="p-3 sm:p-6 max-w-7xl mx-auto">

      {/* Tab bar — sadece list görünümündeyken */}
      {(view === 'list' || view === 'new') && (
        <div className="flex gap-1 bg-gray-100 p-1 rounded-xl mb-4">
          {[
            { key: 'sessions', label: '📦 Tedarik Oturumları' },
            { key: 'report',   label: '📊 Tedarik Raporu' },
            { key: 'proforma', label: '🧾 BSW Proformaları' },
          ].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-colors ${
                tab === t.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}>
              {t.label}
            </button>
          ))}
        </div>
      )}

      {/* Oturumlar tab */}
      {tab === 'sessions' && view === 'list' && (
        <SessionList
          onSelect={s => { setActiveSession(s); setView('session'); }}
          onNew={() => setView('new')}
        />
      )}
      {tab === 'sessions' && view === 'new' && (
        <>
          <SessionList onSelect={s => { setActiveSession(s); setView('session'); }} onNew={() => {}} />
          <NewSessionModal
            onCreated={s => { setActiveSession(s); setView('session'); }}
            onClose={() => setView('list')}
          />
        </>
      )}
      {/* Rapor tab */}
      {tab === 'report' && view !== 'session' && <ScanReport />}
      {tab === 'proforma' && view !== 'session' && <BswProformaListesi />}

      {/* Aktif oturum — tab barı gizlenir */}
      {view === 'session' && activeSession && (
        <ScanSession session={activeSession} onBack={() => setView('list')} />
      )}
    </div>
  );
}
